import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import type { User } from "firebase/auth";
import { auth } from "../../config/firebase";
import styled from "styled-components";
import colors from "@styling/colors";
import { fontSizes } from "@styling/fonts";
import { useTranslation } from "react-i18next";

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  font-size: ${fontSizes.large}px;
  color: ${colors.textMuted};
`;

interface AuthGuardProps {
  children: React.ReactNode;
}

export const AuthGuard = ({ children }: AuthGuardProps) => {
  const { t } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = `${location.pathname}${location.search}`;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
      if (!user) {
        navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`);
      }
    });

    return () => unsubscribe();
  }, [navigate, returnTo]);

  if (loading) {
    return <LoadingContainer>{t("common.loading")}</LoadingContainer>;
  }

  if (!user) {
    return null;
  }

  return <>{children}</>;
};

